import type { PupConfig } from '@/lib/pupConfig';
import { patchesRest, readOrNull } from '@/lib/rest';
import { defineStore } from 'pinia';
import { ref, type Ref } from 'vue';

interface DocumentPage {
  items: { id: string; deleted?: boolean }[];
  next: string | null;
}

export const useConfigStore = defineStore('configStore', () => {
  const config: Ref<PupConfig> = ref(undefined as unknown as PupConfig);

  async function init() {
    // TODO: Is this something we can do in Patches?
    // Tolerant read: a failed config load must not throw out of app mount and
    // abort the rest of init.
    const loaded = await readOrNull(fetchConfig());
    if (loaded) config.value = loaded;
  }

  function fetchConfig() {
    return patchesRest.get('/admin/config').send<PupConfig>();
  }

  // PUT /admin/config replaces the whole document with no validation on pup's side.
  async function saveConfig(next: PupConfig) {
    await patchesRest.put('/admin/config').body(next).send();
    config.value = next;
  }

  // Soft-deleted documents count as data: a restore needs the path the config had when they were written.
  const listDocuments = (collection: string, query: Record<string, string>) =>
    patchesRest
      .get(`/admin/documents/${encodeURIComponent(collection)}`)
      .query({ ...query, includeDeleted: 'true' })
      .send<DocumentPage>();

  async function collectionHasData(collection: string) {
    const page = await listDocuments(collection, { limit: '1' });
    return page.items.length > 0 || page.next !== null;
  }

  // Fixed-name documents (global/plans) share a collection with their siblings, so callers look for
  // their id in the collection's id list. Past `maxPages` the list is incomplete and callers assume data.
  async function collectionIds(collection: string, maxPages = 10) {
    const ids = new Set<string>();
    let cursor: string | null = null;
    for (let page = 0; page < maxPages; page++) {
      const result: DocumentPage = await listDocuments(collection, { limit: '100', ...(cursor ? { cursor } : {}) });
      result.items.forEach(item => ids.add(item.id));
      cursor = result.next;
      if (!cursor) return { ids, complete: true };
    }
    return { ids, complete: false };
  }

  return { config, init, fetchConfig, saveConfig, collectionHasData, collectionIds };
});
